/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {ReservationReducerState} from "store/reservations/ReservationReducer";
import {PersonType} from "types/enums/PersonType";
import {PackageItemPersonType} from "types/enums/PackageItemPersonType";
import {Reservation} from "types/entities/Reservation";
import {ReservationSlot} from "types/entities/ReservationSlot";
import {Ticket} from "types/entities/Ticket";
import {ThunkAction} from "redux-thunk";
import BookingAction from "../store/booking/BookingAction";
import {useDispatch} from "../useDispatch";
import {useTranslation} from "react-i18next";
import Dates from "utils/Dates";
import BookingLoginAction from "store/bookingLogin/BookingLoginAction";
import {useState} from "react";
import {toast} from "react-toastify";

export interface ReservationTicketsState {
    adultRacesLeft: number;
    childRacesLeft: number;
    totalRacesLeft: number;
}

interface ReservationPersons {
    personType: PackageItemPersonType;
    persons_count: number;
}

/**
 * FlatMap of ReservationPersons
 *
 * personType: PackageItemPersonType
 * persons_count: number
 * @returns ReservationPersons[], array of ReservationPersons
 */
const reservationPersonTypes = (reservations): ReservationPersons[] => {
    return reservations
        .filter((reservation: Reservation) => {
            if (!reservation.reservationSlots) {
                return false;
            }
            return reservation.reservationSlots.length > 0;
        })
        .map((reservation: Reservation) => {
            return reservation.reservationSlots.map((reservationSlot: ReservationSlot) => {
                const personType: PackageItemPersonType = reservationSlot.packageItem?.person_type || PackageItemPersonType.Family;
                const reservationPersonsType: ReservationPersons = {
                    personType: personType,
                    persons_count: reservationSlot.persons_count,
                };
                return reservationPersonsType;
            });
        })
        .flatMap(obj => obj);
};

/**
 * Reduce array of ReservationPersons by personTypeValue
 * @param arr
 * @param val
 * @returns number, count of persons
 */
const countPersonsType = (arr: ReservationPersons[], val: PackageItemPersonType) =>
    arr.reduce((a: number, v: ReservationPersons): number => {
        return v.personType == val ? a + v.persons_count : a;
    }, 0);

/**
 * Return a negative value of needed tickets for all outstanding reservations
 * works correctly only if a state has only current reservations
 * @param reservations: Paginator<Reservation>
 */
export const hasEnoughForAll = (reservations): ReservationTicketsState => {
    if (!reservations) {
        return {
            adultRacesLeft: 0,
            childRacesLeft: 0,
            totalRacesLeft: 0,
        };
    }

    const adultRacesLeft = -1 * countPersonsType(reservationPersonTypes(reservations), PackageItemPersonType.Adult);
    const childRacesLeft = -1 * countPersonsType(reservationPersonTypes(reservations), PackageItemPersonType.Child);
    const hasEnoughTicketsForAll = {
        adultRacesLeft: adultRacesLeft,
        childRacesLeft: childRacesLeft,
        totalRacesLeft: adultRacesLeft + childRacesLeft,
    };
    // console.log("hasEnoughTicketsForAll", hasEnoughTicketsForAll);
    return hasEnoughTicketsForAll;
};

/**
 * Return if profile has enough Tickets for current reservation
 *
 */
export const hasEnoughTickets = (tickets: Ticket[], reservation: ReservationReducerState, weekDay?: number | null): ReservationTicketsState => {
    const shiftDate = weekDay ? (+weekDay === 0 ? 7 : weekDay) : null; //fix php to js sundays

    const adultRacesLeft = getTicketRacesLeftInReservation(tickets, reservation, PersonType.Adult, shiftDate);
    const childRacesLeft = getTicketRacesLeftInReservation(tickets, reservation, PersonType.Child, shiftDate);

    const hasEnough: ReservationTicketsState = {
        adultRacesLeft: adultRacesLeft,
        childRacesLeft: childRacesLeft,
        totalRacesLeft: adultRacesLeft + childRacesLeft,
    };
    // console.log("hasEnough", hasEnough);
    return hasEnough;
};

/**
 * Return if profile has enough Tickets for current reservation
 *
 */
export const hasEnoughTicketsWithoutReservation = (tickets: Ticket[], weekDay?: number | null): ReservationTicketsState => {
    const shiftDate = weekDay ? (+weekDay === 0 ? 7 : weekDay) : null; //fix php to js sundays

    const adultRacesLeft = ticketsLeftOnProfile(tickets, PersonType.Adult, weekDay);
    const childRacesLeft = ticketsLeftOnProfile(tickets, PersonType.Child, shiftDate);

    const hasEnough: ReservationTicketsState = {
        adultRacesLeft: adultRacesLeft,
        childRacesLeft: childRacesLeft,
        totalRacesLeft: adultRacesLeft + childRacesLeft,
    };
    // console.log("hasEnough", hasEnough);
    return hasEnough;
};

/**
 * General function that returns sum of objects by their matching keys
 * @param objs
 */
export const sumObjectsByKey = (...objs) => {
    return objs.reduce((a, b) => {
        for (const k in b) {
            if (b.hasOwnProperty(k)) a[k] = (a[k] || 0) + b[k];
        }
        return a;
    }, {});
};

/** Check for tickets available on profile, if there are no tickets, return 0
 *
 * @param tickets
 * @param personType
 * @param weekDay
 */
export const ticketsLeftOnProfile = (tickets: Ticket[], personType?: PersonType, weekDay?: number | null): number => {
    if (!tickets) {
        console.error("No Tickets found in profile");
        return 0;
    }
    return tickets.reduce((agg: number, cur: Ticket) => {
        if (cur.person_type === personType) {
            //if this is found, ticket is not counted in. Should be done by Timeslots (on date change, weekDay => apply only on ), Footer next button (on filter, refresh)

            const isTicketBlacklisted = cur.article?.articleBlacklist && cur.article?.articleBlacklist.length > 0;
            const ticketBlacklistedIndex = cur.article?.articleBlacklist?.findIndex(articleListBlacklist => {
                return articleListBlacklist.day === weekDay;
            });
            // console.log(isTicketBlacklisted, ticketBlacklistedIndex)
            if (!isTicketBlacklisted) {
                // console.log("weekday is given and ticket is not blacklisted, do count in");
                return agg + cur.race_count_left;
            }

            if (typeof weekDay !== "undefined") {
                // count in only tickets, that have blacklist set and are not blacklisted
                if (typeof ticketBlacklistedIndex !== "undefined" && ticketBlacklistedIndex != -1) {
                    //if the blacklisted is found in index, dont count ticket
                    // console.log("dont count current ticket, index found, today is blacklisted", cur);
                    return agg;
                }
                if (isTicketBlacklisted && ticketBlacklistedIndex === -1) {
                    // console.log("count current ticket, index not found, today is not blacklisted", cur);
                    return agg + cur.race_count_left;
                }
            } else {
                // count in only tickets, that have blacklist set and are not blacklisted

                if (isTicketBlacklisted && ticketBlacklistedIndex === -1) {
                    // console.log("weekday is not given and ticket is blacklisted, dont count in");
                    return agg;
                }
            }
            // person type is matched and nothing else (no week day limit on this ticket, e.g. normal ticket
            // console.log("count current ticket", cur);
            return agg + cur.race_count_left;
        }
        //reduce all other unmatched tickets anyway
        return agg;
    }, 0);
};

export const getTicketRacesLeftInReservation = (
    tickets: Ticket[],
    reservation: ReservationReducerState,
    personType: PersonType,
    weekDay?: number | null
): number => {
    // returns tickets left for reservation, but should count in all tickets for that day. This function overwrites per person Type based on the ticket, that is still available, but already has null tickets
    const ticketsLeft = personType => ticketsLeftOnProfile(tickets, personType, weekDay);
    //reservation needs ticket on weekDay, but if this number is negative, we dont have any given tickets on this weekday
    const reservationTicketsNeeded = reservation.drivers
        .filter(driver => driver.person_type === personType)
        .map(driver => driver.races_count)
        .reduce((a, b) => a + b, 0);
    // console.log(ticketsLeft(personType));
    // console.log(reservationTicketsNeeded);
    return ticketsLeft(personType) - reservationTicketsNeeded;
};

export const hasEnoughTicketForProfile = (tickets: Ticket[], profile, reservation: ReservationReducerState, weekDay): boolean => {
    const hasEnough = hasEnoughTickets(tickets, reservation, weekDay);
    switch (profile.person_type) {
        case PersonType.Adult:
            return hasEnough.adultRacesLeft >= enoughTickets.profile;
        case PersonType.Child:
            return hasEnough.childRacesLeft >= enoughTickets.profile;
        default:
            return hasEnough.totalRacesLeft >= enoughTickets.profile;
    }
};
export const uniqueDriversInReservation = data => {
    return data.reduce((acc, obj) => {
        var exist = acc.find(({id}) => obj.id === id);
        if (!exist) {
            acc.push(obj);
        }
        return acc;
    }, []);
};
export const enoughTickets = {notEnough: -1, profile: 0, newRacer: 1};

export const useReservationStatus = (package_cart_guid: string, package_cart_token: string) => {
    const dispatch = useDispatch();
    const getDrivers = () => {
        return dispatch<ThunkAction<any, any, any, any>>(BookingAction.getReservationDrivers(package_cart_guid, package_cart_token)).then(r => {
            return r;
        });
    };

    const getStatus = () => {
        return dispatch<ThunkAction<any, any, any, any>>(BookingAction.getReservationStatus(package_cart_guid, package_cart_token)).then(r => {
            return r;
        });
    };

    return {
        getStatus,
        getDrivers,
    };
};

export const subtractMinutesFromTime = (hour: number, minute: number, subtractMinutes: number) => {
    // Create a new Date object and set the hours and minutes
    const dateObj = new Date();
    dateObj.setHours(hour);
    dateObj.setMinutes(minute);

    // Subtract 15 minutes
    dateObj.setMinutes(dateObj.getMinutes() - subtractMinutes);

    // Get the updated hours and minutes
    const updatedHour = dateObj.getHours();
    const updatedMinute = dateObj.getMinutes();

    // Format the result as "hour:minute"
    const result = `${updatedHour.toString().padStart(2, "0")}:${updatedMinute.toString().padStart(2, "0")}`;

    return result;
};

export const useReservationStatusForLogin = () => {
    const {t} = useTranslation();
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);

    const getStatus = (reservation: Reservation) => {
        const reservationDateTime = new Date(reservation.date).getTime();
        const canEdit = reservationDateTime > midnight.getTime();
        const isReservationPrepared = reservation.persons === reservation?.reservationDrivers?.length;

        if (canEdit) {
            if (isReservationPrepared) {
                return {
                    value: true,
                    label: t("Prepared"),
                    name: "prepared",
                };
            } else {
                return {
                    value: true,
                    label: t("Quick Check-in"),
                    name: "prepare",
                };
            }
        } else {
            return {
                value: false,
                label: t("Completed"),
                name: "completed",
            };
        }
    };

    return {
        getStatus,
    };
};

export const sortReservationsByDate = (reservations: Reservation[]) => {
    return reservations.sort((reservationA, reservationB) => {
        const reservationATime = new Date(reservationA.date).getTime();
        const reservationBTime = new Date(reservationB.date).getTime();
        return reservationBTime - reservationATime;
    });
};

export const getShortNameFromId = (id_string: string) => {
    switch (id_string) {
        case "event_s":
            return "Fun";
        case "event_l":
            return "Ultra";
        case "event_m":
            return "Mega";
        case "general":
            return "Standard";
        case "main":
            return "Standard";
        case "action":
            return "Action / Family";
        case "school":
            return "School";
        case "ferienpass":
            return "Ferien Pass";
        case "kinder":
            return "Kinder";
        case "feriebpass_b":
            return "Ferien Pass Easy";
        case "event_megafun":
            return "Mega Fun";
        case "event_nonplusultra":
            return "Non Plus Ultra";
        default:
            return "Undefined";
    }
};

export interface ReservationPlanTimepickerHoursAndMinutes {
    minuteStep: any;
    disabledHours: () => number[];
    defaultHour: number;
}

export const calculateReservationPlanTimepickerHoursAndMinutes = (
    reservationStartTime: string,
    startHourBefore: number,
    minuteIntervals: number
): ReservationPlanTimepickerHoursAndMinutes => {
    const firstHour = Dates.hour(reservationStartTime) - startHourBefore;
    const disabledHours = () => Array.from({length: firstHour}, (x, i) => i);
    const defaultHour = firstHour;

    return {minuteStep: minuteIntervals, disabledHours: disabledHours, defaultHour: defaultHour};
};

export const useAssignCurrentUserToTicketHandler = () => {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const [isLoading, setIsLoading] = useState<number | null>(null);
    const fetchReservationAssignDriver = async (driver_id: number, packages_cart_item_id: number, reservation_id: number) => {
        try {
            if (isLoading !== null) {
                toast.error(t("Please wait"));
                return;
            }
            setIsLoading(driver_id);
            const r = await dispatch(
                BookingLoginAction.fetchReservationAssignDriver({
                    reservation_id,
                    packages_cart_item_id,
                    driver_id,
                })
            );
            if (r.reservationDriver) {
                dispatch(BookingLoginAction.setReservationDriverToSelectedReservation(r.reservationDriver));
                setIsLoading(null);
            }
            return r;
        } catch (e) {
            console.log(e);
        }
    };

    return {
        fetchReservationAssignDriver,
        isLoading,
    };
};
